<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { toast } from 'dashboard/composables/useToast';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import AddAttribute from './AddAttribute.vue';
import EditAttribute from './EditAttribute.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();

const getters = useStoreGetters();
const store = useStore();
const { currentAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const [showAddPopup, toggleAddPopup] = useToggle(false);
const selectedTabIndex = ref(0);
const uiFlags = computed(() => getters['attributes/getUIFlags'].value);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);
const selectedAttribute = ref({});

const openAddPopup = () => {
  toggleAddPopup(true);
};
const hideAddPopup = () => {
  toggleAddPopup(false);
};
const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
};

// Sem abas: os dois modelos convivem numa TABELA unica, com o alvo
// (conversa/contato) como coluna e filtro. O bug anterior: as pilulas
// passavam o INDICE e o handler lia tab.key — selectedTabIndex virava
// undefined e a troca parava de responder.
const MODEL_FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'conversation_attribute', label: 'Conversa' },
  { key: 'contact_attribute', label: 'Contato' },
];
const modelFilter = ref('all');

onMounted(() => {
  store.dispatch('attributes/get');
});

const attributes = computed(() => {
  const porModelo = getters['attributes/getAttributesByModel'].value;
  return [
    ...porModelo('conversation_attribute'),
    ...porModelo('contact_attribute'),
  ];
});

const handleEditAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleEditPopup(true);
};

const handleDeleteAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleDeletePopup(true);
};

const confirmDeleteAttribute = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    toast.success(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDelete();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE');
    toast.error(errorMessage);
  }
};

const requiredAttributeKeys = computed(
  () => currentAccount.value?.settings?.conversation_required_attributes || []
);

const hasPreChatBadge = attribute => {
  return (inboxes.value || []).some(inbox => {
    const fields =
      inbox?.pre_chat_form_options?.pre_chat_fields ||
      inbox?.channel?.pre_chat_form_options?.pre_chat_fields ||
      [];
    return fields.some(field => field.name === attribute.attribute_key);
  });
};

const buildBadges = attribute => {
  const badges = [];
  if (hasPreChatBadge(attribute)) {
    badges.push({
      type: 'pre-chat',
    });
  }

  if (
    attribute.attribute_model === 'conversation_attribute' &&
    requiredAttributeKeys.value.includes(attribute.attribute_key)
  ) {
    badges.push({
      type: 'resolution',
    });
  }

  return badges;
};

const attributeSearch = ref('');

const derivedAttributes = computed(() =>
  attributes.value.map(attribute => ({
    ...attribute,
    label: attribute.attribute_display_name,
    type: attribute.attribute_display_type,
    value: attribute.attribute_key,
    badges: buildBadges(attribute),
  }))
);

const visibleAttributes = computed(() => {
  const q = attributeSearch.value.trim().toLowerCase();
  return derivedAttributes.value.filter(a => {
    if (
      modelFilter.value !== 'all' &&
      a.attribute_model !== modelFilter.value
    ) {
      return false;
    }
    if (!q) return true;
    return (
      a.label?.toLowerCase().includes(q) || a.value?.toLowerCase().includes(q)
    );
  });
});

const TYPE_LABELS = {
  text: 'Texto',
  number: 'Número',
  currency: 'Moeda',
  percent: 'Percentual',
  link: 'Link',
  date: 'Data',
  list: 'Lista',
  checkbox: 'Sim/Não',
};
const typeLabel = type => TYPE_LABELS[String(type).toLowerCase()] || type;
</script>

<template>
  <SettingsPageLayout
    :title="$t('ATTRIBUTES_MGMT.HEADER')"
    :description="$t('ATTRIBUTES_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('ATTRIBUTES_MGMT.HEADER_BTN_TXT')"
        @click="openAddPopup"
      />
    </template>

    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-12"
    >
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
    </div>

    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <div v-else class="flex flex-col gap-4">
      <div class="flex items-center gap-1.5 flex-wrap">
        <button
          v-for="f in MODEL_FILTERS"
          :key="f.key"
          class="rounded-full px-3.5 py-1.5 text-xs border transition-colors"
          :class="
            modelFilter === f.key
              ? 'bg-n-slate-12 border-n-slate-12 text-n-solid-1 font-medium'
              : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:bg-n-slate-2'
          "
          @click="modelFilter = f.key"
        >
          {{ f.label }}
        </button>
        <input
          v-model="attributeSearch"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.SEARCH_PLACEHOLDER')"
          class="!mb-0 !h-7 w-48 rounded-full text-xs ml-auto"
        />
      </div>

      <div
        v-if="visibleAttributes.length"
        class="rounded-xl bg-n-solid-1 outline outline-1 outline-n-container overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="w-full text-left">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="px-4 py-2.5 pl-6 text-[10.5px] font-bold uppercase tracking-[0.05em] text-n-slate-9"
                >
                  Nome
                </th>
                <th
                  class="px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-n-slate-9"
                >
                  Chave
                </th>
                <th
                  class="px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-n-slate-9"
                >
                  Tipo
                </th>
                <th
                  class="px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-n-slate-9"
                >
                  Aplica-se a
                </th>
                <th
                  class="px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-n-slate-9"
                >
                  Uso
                </th>
                <th class="w-24 pr-6" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="attribute in visibleAttributes"
                :key="attribute.id"
                class="group border-b border-n-weak last:border-b-0 hover:bg-n-solid-2"
              >
                <td class="px-4 py-2.5 pl-6">
                  <b class="block text-[12.5px] font-semibold text-n-slate-12">
                    {{ attribute.label }}
                  </b>
                  <span
                    v-if="attribute.attribute_description"
                    class="block max-w-[38ch] truncate text-[11px] text-n-slate-9"
                  >
                    {{ attribute.attribute_description }}
                  </span>
                </td>
                <td class="px-4 py-2.5">
                  <code
                    class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[11px] text-n-slate-11"
                  >
                    {{ attribute.value }}
                  </code>
                </td>
                <td class="px-4 py-2.5 text-xs text-n-slate-11">
                  {{ typeLabel(attribute.type) }}
                </td>
                <td class="px-4 py-2.5">
                  <span
                    class="rounded border px-1.5 py-px text-[10.5px] font-semibold"
                    :class="
                      attribute.attribute_model === 'contact_attribute'
                        ? 'border-n-blue-5 bg-n-blue-3 text-n-blue-11'
                        : 'border-n-weak bg-n-slate-3 text-n-slate-10'
                    "
                  >
                    {{
                      attribute.attribute_model === 'contact_attribute'
                        ? 'Contato'
                        : 'Conversa'
                    }}
                  </span>
                </td>
                <td class="px-4 py-2.5">
                  <span class="flex gap-1 flex-wrap">
                    <span
                      v-for="badge in attribute.badges"
                      :key="badge.type"
                      class="rounded bg-n-amber-3 px-1.5 py-px text-[10px] font-semibold text-n-amber-11"
                    >
                      {{
                        badge.type === 'pre-chat'
                          ? 'formulário pré-chat'
                          : 'obrigatório p/ resolver'
                      }}
                    </span>
                    <span
                      v-if="!attribute.badges.length"
                      class="text-xs text-n-slate-8"
                    >
                      —
                    </span>
                  </span>
                </td>
                <td class="px-4 py-2.5 pr-6">
                  <span
                    class="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <Button
                      icon="i-lucide-pen"
                      slate
                      xs
                      faded
                      @click="handleEditAttribute(attribute)"
                    />
                    <Button
                      icon="i-lucide-trash-2"
                      xs
                      ruby
                      faded
                      @click="handleDeleteAttribute(attribute)"
                    />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p
        v-else
        class="flex-1 py-16 text-n-slate-10 flex items-center justify-center text-sm"
      >
        {{ $t('ATTRIBUTES_MGMT.LIST.EMPTY_RESULT.404') }}
      </p>
    </div>

    <AddAttribute
      v-if="showAddPopup"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
      :selected-attribute-model-tab="selectedTabIndex"
    />
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAttribute
        :selected-attribute="selectedAttribute"
        :is-updating="uiFlags.isUpdating"
        @on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      :message="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="`${$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.YES')} ${
        selectedAttribute.attribute_display_name || ''
      }`"
      :reject-text="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.NO')"
      :confirm-value="selectedAttribute.attribute_display_name"
      :confirm-place-holder-text="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      @on-confirm="confirmDeleteAttribute"
      @on-close="closeDelete"
    />
  </SettingsPageLayout>
</template>
